import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { childrenAPI, profileAPI, conversationAPI, insightsAPI, safetyEventsAPI, summariesAPI, childSettingsAPI, activitiesAPI, metricsAPI, emailPreferencesAPI } from '../services/api';
import { APP_NAME } from '../constants/config';
import { InsightTypePieChart, WeeklyTrendChart } from '../components/InsightCharts';
import { DailyMetricsChart, WeeklyMetricsChart, MetricsSummaryCard, MessageDistributionChart } from '../components/MetricsCharts';
import './Dashboard.css';

// Overview Component
function Overview({ selectedChild, children }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [insightsSummary, setInsightsSummary] = useState(null);
  const [insightsTimeRange, setInsightsTimeRange] = useState(30);
  const [selectedInsightType, setSelectedInsightType] = useState(null);
  const [filteredInsights, setFilteredInsights] = useState([]);

  // Metrics state
  const [dailyMetrics, setDailyMetrics] = useState(null);
  const [weeklyMetrics, setWeeklyMetrics] = useState(null);
  const [sessionHistory, setSessionHistory] = useState(null);
  const [metricsView, setMetricsView] = useState('weekly'); // 'daily', 'sessions', or 'weekly'
  const [metricsTimeRange, setMetricsTimeRange] = useState(7); // 7 days or 4 weeks

  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      const [conversationsRes, insightsRes, safetyRes] = await Promise.all([
        conversationAPI.getHistory(selectedChild.id, 100),
        insightsAPI.get(selectedChild.id, 10),
        safetyEventsAPI.get(selectedChild.id, 50),
      ]);

      // Get today's date in client's local timezone
      const today = new Date();
      const todayYear = today.getFullYear();
      const todayMonth = today.getMonth();
      const todayDay = today.getDate();

      const todayMessages = conversationsRes.data.messages.filter(msg => {
        const msgDate = new Date(msg.created_at);
        return msgDate.getFullYear() === todayYear &&
               msgDate.getMonth() === todayMonth &&
               msgDate.getDate() === todayDay;
      });

      setStats({
        totalMessages: conversationsRes.data.total_messages,
        todayMessages: todayMessages.length,
        totalInsights: insightsRes.data.total_insights,
        safetyEvents24h: safetyRes.data.events_in_last_24h,
        totalSafetyEvents: safetyRes.data.total_events,
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedChild]);

  useEffect(() => {
    if (selectedChild) {
      loadStats();
    }
  }, [selectedChild, loadStats]);

  // Load insights summary
  useEffect(() => {
    const loadInsightsSummary = async () => {
      if (selectedChild) {
        try {
          const response = await insightsAPI.getSummary(selectedChild.id, insightsTimeRange);
          setInsightsSummary(response.data);
          // Reset filter when time range changes
          setSelectedInsightType(null);
          setFilteredInsights([]);
        } catch (error) {
          console.error('Failed to load insights summary:', error);
        }
      }
    };

    loadInsightsSummary();
  }, [selectedChild, insightsTimeRange]);

  // Load metrics data
  useEffect(() => {
    const loadMetrics = async () => {
      if (selectedChild) {
        try {
          if (metricsView === 'daily') {
            const response = await metricsAPI.getDaily(selectedChild.id, metricsTimeRange);
            setDailyMetrics(response.data);
            setWeeklyMetrics(null);
            setSessionHistory(null);
          } else if (metricsView === 'sessions') {
            const response = await metricsAPI.getSessions(selectedChild.id, metricsTimeRange);
            setSessionHistory(response.data);
            setDailyMetrics(null);
            setWeeklyMetrics(null);
          } else {
            const response = await metricsAPI.getWeekly(selectedChild.id, metricsTimeRange);
            setWeeklyMetrics(response.data);
            setDailyMetrics(null);
            setSessionHistory(null);
          }
        } catch (error) {
          console.error('Failed to load metrics:', error);
        }
      }
    };

    loadMetrics();
  }, [selectedChild, metricsView, metricsTimeRange]);

  // Handle clicking on a pie chart type
  const handleInsightTypeClick = async (type) => {
    if (selectedInsightType === type) {
      // Clicking the same type again clears the filter
      setSelectedInsightType(null);
      setFilteredInsights([]);
    } else {
      // Fetch all insights of this type
      setSelectedInsightType(type);
      try {
        const response = await insightsAPI.get(selectedChild.id, 100, type);
        setFilteredInsights(response.data.insights || []);
      } catch (error) {
        console.error('Failed to load filtered insights:', error);
      }
    }
  };

  if (!selectedChild) {
    return (
      <div className="empty-state">
        <h3>No child selected</h3>
        <p>Please select or add a child to view the dashboard</p>
      </div>
    );
  }

  if (loading) {
    return <div className="loading-spinner">Loading statistics...</div>;
  }

  return (
    <div>
      <div className="section-header">
        <h3>Overview - {selectedChild.name}</h3>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <h4>Messages Today</h4>
          <p className="stat-value">{stats?.todayMessages || 0}</p>
        </div>
        <div className="stat-card">
          <h4>Total Messages</h4>
          <p className="stat-value">{stats?.totalMessages || 0}</p>
        </div>
        <div className="stat-card">
          <h4>Insights</h4>
          <p className="stat-value">{stats?.totalInsights || 0}</p>
        </div>
        <div className="stat-card" style={{ background: stats?.safetyEvents24h > 0 ? 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' : 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
          <h4>Safety Alerts (24h)</h4>
          <p className="stat-value">{stats?.safetyEvents24h || 0}</p>
        </div>
      </div>

      <div className="action-buttons">
        <button
          className="chat-button"
          onClick={() => navigate(`/chat/${selectedChild.id}`)}
        >
          💬 Start Chat with {selectedChild.name}
        </button>
      </div>

      {/* Insights Summary Section */}
      {insightsSummary && (
        <div className="insights-summary-section">
          <div className="section-header-with-filter">
            <h3>Insights Summary</h3>
            <select
              value={insightsTimeRange}
              onChange={(e) => setInsightsTimeRange(Number(e.target.value))}
              className="time-range-select"
            >
              <option value={7}>Last 7 Days</option>
              <option value={30}>Last 30 Days</option>
              <option value={90}>Last 90 Days</option>
            </select>
          </div>

          <div className="insights-summary-grid">
            {/* Daily Activity */}
            <div className="chart-card">
              <h4>Daily Activity</h4>
              <WeeklyTrendChart data={insightsSummary.by_week} />
            </div>

            {/* Pie Chart */}
            <div className="chart-card">
              <h4>Insights by Type</h4>
              <p style={{ fontSize: '12px', color: '#999', marginTop: '-10px', marginBottom: '10px' }}>
                Click on a section to filter insights
              </p>
              <InsightTypePieChart
                data={insightsSummary.by_type}
                onTypeClick={handleInsightTypeClick}
                selectedType={selectedInsightType}
              />
            </div>

            {/* Recent Insights / Filtered Insights */}
            <div className="recent-insights-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h4 style={{ margin: 0 }}>
                  {selectedInsightType
                    ? `${selectedInsightType.charAt(0).toUpperCase() + selectedInsightType.slice(1)} Insights`
                    : 'Recent Insights'}
                </h4>
                {selectedInsightType && (
                  <button
                    onClick={() => {
                      setSelectedInsightType(null);
                      setFilteredInsights([]);
                    }}
                    style={{
                      padding: '6px 12px',
                      background: '#667eea',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      fontWeight: '600'
                    }}
                  >
                    Clear Filter
                  </button>
                )}
              </div>
              {(selectedInsightType ? filteredInsights : insightsSummary.recent_insights).length === 0 ? (
                <p className="no-data">
                  {selectedInsightType
                    ? `No ${selectedInsightType} insights yet.`
                    : 'No insights yet. Start chatting to generate insights!'}
                </p>
              ) : (
                <div className="insights-timeline">
                  {(selectedInsightType ? filteredInsights : insightsSummary.recent_insights).map(insight => (
                    <div key={insight.id} className="insight-timeline-item">
                      <span className={`insight-tag ${insight.type}`}>
                        {insight.type.toUpperCase()}
                      </span>
                      <span className="insight-text">{insight.text}</span>
                      <span className="insight-date">
                        {new Date(insight.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Metrics Section */}
      <div className="metrics-section">
        <div className="section-header-with-filter">
          <h3>Progress Metrics</h3>
          <div className="metrics-controls">
            <div className="view-toggle">
              <button
                className={metricsView === 'weekly' ? 'active' : ''}
                onClick={() => {
                  setMetricsView('weekly');
                  setMetricsTimeRange(4);
                }}
              >
                Weekly
              </button>
              <button
                className={metricsView === 'daily' ? 'active' : ''}
                onClick={() => {
                  setMetricsView('daily');
                  setMetricsTimeRange(7);
                }}
              >
                Daily
              </button>
              <button
                className={metricsView === 'sessions' ? 'active' : ''}
                onClick={() => {
                  setMetricsView('sessions');
                  setMetricsTimeRange(7);
                }}
              >
                Sessions
              </button>
            </div>
            <select
              value={metricsTimeRange}
              onChange={(e) => setMetricsTimeRange(Number(e.target.value))}
              className="time-range-select"
            >
              {metricsView === 'weekly' ? (
                <>
                  <option value={4}>Last 4 Weeks</option>
                  <option value={8}>Last 8 Weeks</option>
                  <option value={12}>Last 12 Weeks</option>
                </>
              ) : (
                <>
                  <option value={7}>Last 7 Days</option>
                  <option value={14}>Last 14 Days</option>
                  <option value={30}>Last 30 Days</option>
                </>
              )}
            </select>
          </div>
        </div>

        {/* Summary Cards */}
        {(dailyMetrics?.metrics || weeklyMetrics?.metrics) && (
          <div className="metrics-summary-grid">
            {metricsView === 'daily' && dailyMetrics?.metrics?.[0] && (
              <>
                <MetricsSummaryCard
                  title="Sessions Today"
                  value={dailyMetrics.metrics[0].session_count || 0}
                  icon="🔄"
                />
                <MetricsSummaryCard
                  title="Avg Engagement"
                  value={dailyMetrics.metrics[0].engagement_score || 'N/A'}
                  unit="/100"
                  icon="⭐"
                />
                <MetricsSummaryCard
                  title="Total Messages"
                  value={dailyMetrics.metrics[0].total_messages || 0}
                  icon="💬"
                />
                <MetricsSummaryCard
                  title="Help Clicks"
                  value={dailyMetrics.metrics[0].help_button_clicks || 0}
                  icon="🆘"
                />
              </>
            )}
            {metricsView === 'weekly' && weeklyMetrics?.metrics?.[0] && (
              <>
                <MetricsSummaryCard
                  title="Avg Engagement"
                  value={weeklyMetrics.metrics[0].avg_engagement_score || 'N/A'}
                  unit="/100"
                  trend={weeklyMetrics.metrics[0].improvement_percentage}
                  icon="⭐"
                />
                <MetricsSummaryCard
                  title="Total Sessions"
                  value={weeklyMetrics.metrics[0].total_sessions || 0}
                  icon="📅"
                />
                <MetricsSummaryCard
                  title="Help Button Clicks"
                  value={weeklyMetrics.metrics[0].total_help_clicks || 0}
                  icon="🆘"
                />
                <MetricsSummaryCard
                  title="Total Messages"
                  value={weeklyMetrics.metrics[0].total_messages || 0}
                  icon="💬"
                />
              </>
            )}
          </div>
        )}

        {/* Metrics Chart */}
        <div className="metrics-chart-container">
          {metricsView === 'daily' && dailyMetrics && (
            <div className="chart-card">
              <h4>Daily Progress Trend</h4>
              <DailyMetricsChart data={dailyMetrics.metrics} />
            </div>
          )}
          {metricsView === 'weekly' && weeklyMetrics && (
            <div className="chart-card">
              <h4>Weekly Progress Summary</h4>
              <WeeklyMetricsChart data={weeklyMetrics.metrics} />
            </div>
          )}
          {metricsView === 'sessions' && sessionHistory && (
            <div className="sessions-list">
              <h4>Individual Sessions ({sessionHistory.sessions.length})</h4>
              {sessionHistory.sessions.length === 0 ? (
                <p className="no-data">No sessions found in this time period.</p>
              ) : (
                <div className="sessions-grid">
                  {sessionHistory.sessions.map((session, idx) => (
                    <div key={session.id} className="session-card">
                      <div className="session-header">
                        <span className="session-number">Session #{sessionHistory.sessions.length - idx}</span>
                        <span className="session-date">
                          {new Date(session.session_date).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </span>
                      </div>
                      {session.session_started_at && (
                        <div className="session-time">
                          {new Date(session.session_started_at).toLocaleTimeString('en-US', {
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true
                          })}
                          {session.session_ended_at && ` - ${new Date(session.session_ended_at).toLocaleTimeString('en-US', {
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true
                          })}`}
                        </div>
                      )}
                      <div className="session-stats">
                        <div className="stat">
                          <span className="stat-label">Engagement</span>
                          <span className="stat-value engagement">
                            {session.engagement_score || 'N/A'}
                            {session.engagement_score && '/100'}
                          </span>
                        </div>
                        <div className="stat">
                          <span className="stat-label">Messages</span>
                          <span className="stat-value">{session.total_messages}</span>
                        </div>
                        <div className="stat">
                          <span className="stat-label">Duration</span>
                          <span className="stat-value">{session.session_duration_minutes || 0} min</span>
                        </div>
                        <div className="stat">
                          <span className="stat-label">Help Clicks</span>
                          <span className="stat-value">{session.help_button_clicks}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

// Summaries Component
function Summaries({ selectedChild }) {
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSummary, setSelectedSummary] = useState(null);

  const loadSummaries = useCallback(async () => {
    setLoading(true);
    try {
      const response = await summariesAPI.getAll(selectedChild.id, 30);
      setSummaries(response.data.summaries);
    } catch (error) {
      console.error('Error loading summaries:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedChild]);

  useEffect(() => {
    if (selectedChild) {
      loadSummaries();
    }
  }, [selectedChild, loadSummaries]);

  const generateSummary = async () => {
    try {
      setLoading(true);
      await summariesAPI.generate(selectedChild.id);
      await loadSummaries();
      alert('Summary generated successfully!');
    } catch (error) {
      console.error('Error generating summary:', error);
      alert('Failed to generate summary. Make sure there are conversations for today.');
    }
  };

  if (!selectedChild) {
    return <div className="empty-state"><h3>Please select a child</h3></div>;
  }

  if (loading) {
    return <div className="loading-spinner">Loading summaries...</div>;
  }

  return (
    <div>
      <div className="section-header">
        <h3>Conversation Summaries</h3>
        <button onClick={generateSummary} className="button button-primary">
          Generate Today's Summary
        </button>
      </div>

      {summaries.length === 0 ? (
        <div className="empty-state">
          <h3>No summaries yet</h3>
          <p>Generate a summary to see daily conversation insights</p>
        </div>
      ) : (
        <ul className="summary-list">
          {summaries.map((summary) => (
            <li
              key={summary.id}
              className="summary-item"
              onClick={() => setSelectedSummary(selectedSummary?.id === summary.id ? null : summary)}
            >
              <h4>{new Date(summary.date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</h4>
              <p>{selectedSummary?.id === summary.id ? summary.convo_summary : summary.convo_summary.substring(0, 150) + '...'}</p>
              {selectedSummary?.id === summary.id && (
                <div style={{ marginTop: '10px', fontSize: '12px', color: '#3498db' }}>
                  Click to collapse
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Safety Alerts Component
function SafetyAlerts({ selectedChild }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const response = await safetyEventsAPI.get(selectedChild.id, 50);
      setEvents(response.data.events);
    } catch (error) {
      console.error('Error loading safety events:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedChild]);

  useEffect(() => {
    if (selectedChild) {
      loadEvents();
    }
  }, [selectedChild, loadEvents]);

  if (!selectedChild) {
    return <div className="empty-state"><h3>Please select a child</h3></div>;
  }

  if (loading) {
    return <div className="loading-spinner">Loading safety alerts...</div>;
  }

  const getSeverityClass = (eventType) => {
    if (eventType === 'keyword_trigger' || eventType === 'emotional_distress') return '';
    if (eventType === 'inappropriate_topic') return 'warning';
    return 'info';
  };

  return (
    <div>
      <div className="section-header">
        <h3>Safety Alerts</h3>
      </div>

      {events.length === 0 ? (
        <div className="empty-state">
          <h3>No safety alerts</h3>
          <p>Great news! No safety concerns have been detected.</p>
        </div>
      ) : (
        <div>
          {events.map((event) => (
            <div key={event.id} className={`alert-item ${getSeverityClass(event.event_type)}`}>
              <h4>{event.event_type.replace('_', ' ').toUpperCase()}</h4>
              <p>{event.raw_text}</p>
              <div className="alert-time">
                {new Date(event.created_at).toLocaleString()}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Child Profile Component
function ChildProfile({ selectedChild, onUpdate }) {
  const [profile, setProfile] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newFavorite, setNewFavorite] = useState('');
  const [newDislike, setNewDislike] = useState('');
  const [newSensitivity, setNewSensitivity] = useState('');

  const loadProfile = useCallback(async () => {
    setLoading(true);
    try {
      const [profileRes, activitiesRes] = await Promise.all([
        profileAPI.get(selectedChild.id),
        activitiesAPI.get(selectedChild.id, 20)
      ]);
      setProfile(profileRes.data);
      setActivities(activitiesRes.data.activities || []);
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedChild]);

  useEffect(() => {
    if (selectedChild) {
      loadProfile();
    }
  }, [selectedChild, loadProfile]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await profileAPI.update(selectedChild.id, profile);
      alert('Profile updated successfully!');
    } catch (error) {
      console.error('Error saving profile:', error);
      alert('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const addItem = (field, value, setter) => {
    if (value.trim()) {
      setProfile({
        ...profile,
        [field]: [...profile[field], value.trim()]
      });
      setter('');
    }
  };

  const removeItem = (field, index) => {
    setProfile({
      ...profile,
      [field]: profile[field].filter((_, i) => i !== index)
    });
  };

  if (!selectedChild) {
    return <div className="empty-state"><h3>Please select a child</h3></div>;
  }

  if (loading) {
    return <div className="loading-spinner">Loading profile...</div>;
  }

  return (
    <div>
      <div className="section-header">
        <h3>Child Profile - {selectedChild.name}</h3>
        <button onClick={handleSave} className="button button-primary" disabled={saving}>
          {saving ? 'Saving...' : 'Save Profile'}
        </button>
      </div>

      <div className="profile-form">
        <div className="form-group">
          <label>Favorite Topics / Interests</label>
          <div style={{ display: 'flex', gap: '10px' }}>
            <input
              type="text"
              className="input"
              value={newFavorite}
              onChange={(e) => setNewFavorite(e.target.value)}
              placeholder="Type here and press Enter or click Add"
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addItem('favorites', newFavorite, setNewFavorite))}
              style={{ flex: 1 }}
            />
            <button
              onClick={() => addItem('favorites', newFavorite, setNewFavorite)}
              className="button button-primary"
              style={{ minWidth: '100px' }}
            >
              ✓ Add
            </button>
          </div>
          <p style={{ fontSize: '12px', color: '#666', margin: '5px 0 10px 0' }}>
            💡 Tip: Add one at a time by typing and pressing Enter or clicking Add
          </p>
          <div className="tag-list">
            {profile.favorites.map((fav, index) => (
              <span key={index} className="tag">
                {fav}
                <button onClick={() => removeItem('favorites', index)}>×</button>
              </span>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label>Dislikes</label>
          <div style={{ display: 'flex', gap: '10px' }}>
            <input
              type="text"
              className="input"
              value={newDislike}
              onChange={(e) => setNewDislike(e.target.value)}
              placeholder="Type here and press Enter or click Add"
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addItem('dislikes', newDislike, setNewDislike))}
              style={{ flex: 1 }}
            />
            <button
              onClick={() => addItem('dislikes', newDislike, setNewDislike)}
              className="button button-primary"
              style={{ minWidth: '100px' }}
            >
              ✓ Add
            </button>
          </div>
          <p style={{ fontSize: '12px', color: '#666', margin: '5px 0 10px 0' }}>
            💡 Tip: Add one at a time by typing and pressing Enter or clicking Add
          </p>
          <div className="tag-list">
            {profile.dislikes.map((dislike, index) => (
              <span key={index} className="tag" style={{ background: '#e74c3c' }}>
                {dislike}
                <button onClick={() => removeItem('dislikes', index)}>×</button>
              </span>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label>Sensitivities</label>
          <div style={{ display: 'flex', gap: '10px' }}>
            <input
              type="text"
              className="input"
              value={newSensitivity}
              onChange={(e) => setNewSensitivity(e.target.value)}
              placeholder="Type here and press Enter or click Add"
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addItem('sensitivities', newSensitivity, setNewSensitivity))}
              style={{ flex: 1 }}
            />
            <button
              onClick={() => addItem('sensitivities', newSensitivity, setNewSensitivity)}
              className="button button-primary"
              style={{ minWidth: '100px' }}
            >
              ✓ Add
            </button>
          </div>
          <p style={{ fontSize: '12px', color: '#666', margin: '5px 0 10px 0' }}>
            💡 Tip: Add one at a time by typing and pressing Enter or clicking Add
          </p>
          <div className="tag-list">
            {profile.sensitivities.map((sensitivity, index) => (
              <span key={index} className="tag" style={{ background: '#f39c12' }}>
                {sensitivity}
                <button onClick={() => removeItem('sensitivities', index)}>×</button>
              </span>
            ))}
          </div>
        </div>

        {/* Activities Section */}
        <div className="profile-section" style={{ marginTop: '30px', padding: '20px', background: '#f8f9fa', borderRadius: '8px' }}>
          <h4 style={{ marginBottom: '15px' }}>Suggested Activities</h4>
          {activities.length === 0 ? (
            <p style={{ color: '#999' }}>No activities suggested yet. Keep chatting to receive activity ideas!</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              {activities.map((activity) => (
                <div key={activity.id} style={{
                  padding: '16px',
                  background: 'white',
                  borderRadius: '8px',
                  borderLeft: `4px solid ${
                    activity.category === 'physical' ? '#3498db' :
                    activity.category === 'creative' ? '#9b59b6' :
                    activity.category === 'social' ? '#f39c12' :
                    activity.category === 'learning' ? '#2ecc71' : '#e91e63'
                  }`
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                    <span style={{ fontSize: '28px' }}>{activity.icon || '✨'}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                        <h5 style={{ margin: 0, fontSize: '16px', fontWeight: '700' }}>
                          {activity.title}
                        </h5>
                        {activity.category && (
                          <span style={{
                            fontSize: '11px',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontWeight: '600',
                            textTransform: 'uppercase',
                            background: activity.category === 'physical' ? '#e3f2fd' :
                              activity.category === 'creative' ? '#f3e5f5' :
                              activity.category === 'social' ? '#fff3e0' :
                              activity.category === 'learning' ? '#e8f5e9' : '#fce4ec',
                            color: activity.category === 'physical' ? '#1976d2' :
                              activity.category === 'creative' ? '#7b1fa2' :
                              activity.category === 'social' ? '#f57c00' :
                              activity.category === 'learning' ? '#388e3c' : '#c2185b'
                          }}>
                            {activity.category}
                          </span>
                        )}
                      </div>
                      <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#666', lineHeight: '1.6' }}>
                        {activity.description}
                      </p>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: '12px', color: '#999' }}>
                          {new Date(activity.created_at).toLocaleDateString()}
                        </span>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            style={{
                              padding: '6px 12px',
                              border: activity.parent_marked_done ? '2px solid #667eea' : '2px solid #e0e0e0',
                              background: activity.parent_marked_done ? '#667eea' : 'white',
                              color: activity.parent_marked_done ? 'white' : '#666',
                              borderRadius: '6px',
                              fontSize: '13px',
                              cursor: 'pointer',
                              fontWeight: '600',
                              transition: 'all 0.2s'
                            }}
                            onClick={async () => {
                              try {
                                await activitiesAPI.updateStatus(
                                  selectedChild.id,
                                  activity.id,
                                  { parent_marked_done: !activity.parent_marked_done }
                                );
                                loadProfile();
                              } catch (error) {
                                console.error('Failed to update activity:', error);
                              }
                            }}
                          >
                            ✓ Done
                          </button>
                          <button
                            style={{
                              padding: '6px 12px',
                              border: activity.parent_favorited ? '2px solid #f39c12' : '2px solid #e0e0e0',
                              background: activity.parent_favorited ? '#f39c12' : 'white',
                              color: activity.parent_favorited ? 'white' : '#666',
                              borderRadius: '6px',
                              fontSize: '13px',
                              cursor: 'pointer',
                              fontWeight: '600',
                              transition: 'all 0.2s'
                            }}
                            onClick={async () => {
                              try {
                                await activitiesAPI.updateStatus(
                                  selectedChild.id,
                                  activity.id,
                                  { parent_favorited: !activity.parent_favorited }
                                );
                                loadProfile();
                              } catch (error) {
                                console.error('Failed to update activity:', error);
                              }
                            }}
                          >
                            ⭐ Favorite
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Conversation History Component
function ConversationHistory({ selectedChild }) {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadConversations = useCallback(async () => {
    setLoading(true);
    try {
      const response = await conversationAPI.getHistory(selectedChild.id, 100);
      setConversations(response.data.messages);
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedChild]);

  useEffect(() => {
    if (selectedChild) {
      loadConversations();
    }
  }, [selectedChild, loadConversations]);

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete all conversation history? This cannot be undone.')) {
      try {
        await conversationAPI.deleteHistory(selectedChild.id);
        setConversations([]);
        alert('Conversation history deleted successfully');
      } catch (error) {
        console.error('Error deleting conversations:', error);
        alert('Failed to delete conversation history');
      }
    }
  };

  if (!selectedChild) {
    return <div className="empty-state"><h3>Please select a child</h3></div>;
  }

  if (loading) {
    return <div className="loading-spinner">Loading conversations...</div>;
  }

  return (
    <div>
      <div className="section-header">
        <h3>Conversation History</h3>
        <button onClick={handleDelete} className="button button-danger">
          Delete All History
        </button>
      </div>

      {conversations.length === 0 ? (
        <div className="empty-state">
          <h3>No conversations yet</h3>
          <p>Conversations will appear here once your child starts chatting with the AI companion</p>
        </div>
      ) : (
        <div className="conversation-list">
          {conversations.map((msg, index) => (
            <div key={index} className={`conversation-message ${msg.role}`}>
              <div className="message-header">
                <strong>{msg.role === 'user' ? selectedChild.name : APP_NAME}</strong>
                <span>{new Date(msg.created_at).toLocaleString()}</span>
              </div>
              <div className="message-content">{msg.content}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Parent Controls Component
function ParentControls({ selectedChild }) {
  const [settings, setSettings] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newBlockedTopic, setNewBlockedTopic] = useState('');
  const [newAllowedTopic, setNewAllowedTopic] = useState('');

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const [settingsRes, usageRes] = await Promise.all([
        childSettingsAPI.get(selectedChild.id),
        childSettingsAPI.getSessionUsage(selectedChild.id)
      ]);
      setSettings(settingsRes.data);
      setUsage(usageRes.data);
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedChild]);

  useEffect(() => {
    if (selectedChild) {
      loadSettings();
    }
  }, [selectedChild, loadSettings]);

  const handleToggleAI = async () => {
    setSaving(true);
    try {
      const response = await childSettingsAPI.toggleAI(selectedChild.id, !settings.ai_enabled);
      setSettings(response.data);
      alert(`AI ${response.data.ai_enabled ? 'enabled' : 'disabled'} successfully!`);
    } catch (error) {
      console.error('Error toggling AI:', error);
      alert('Failed to toggle AI');
    } finally {
      setSaving(false);
    }
  };

  const handleSetLimits = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const formData = new FormData(e.target);
      const timeLimit = formData.get('timeLimit') || null;
      const messageLimit = formData.get('messageLimit') || null;

      const response = await childSettingsAPI.setSessionLimits(selectedChild.id, {
        daily_time_limit_minutes: timeLimit ? parseInt(timeLimit) : null,
        daily_message_limit: messageLimit ? parseInt(messageLimit) : null
      });
      setSettings(response.data);
      alert('Session limits updated successfully!');
      await loadSettings(); // Refresh usage
    } catch (error) {
      console.error('Error setting limits:', error);
      alert('Failed to update limits');
    } finally {
      setSaving(false);
    }
  };

  const handleAddBlockedTopic = async () => {
    if (!newBlockedTopic.trim()) return;
    setSaving(true);
    try {
      const updatedTopics = [...settings.blocked_topics, newBlockedTopic.trim()];
      const response = await childSettingsAPI.manageTopics(selectedChild.id, {
        blocked_topics: updatedTopics,
        allowed_topics_only: settings.allowed_topics_only,
        allowed_topics: settings.allowed_topics
      });
      setSettings(response.data);
      setNewBlockedTopic('');
    } catch (error) {
      console.error('Error adding blocked topic:', error);
      alert('Failed to add blocked topic');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveBlockedTopic = async (index) => {
    setSaving(true);
    try {
      const updatedTopics = settings.blocked_topics.filter((_, i) => i !== index);
      const response = await childSettingsAPI.manageTopics(selectedChild.id, {
        blocked_topics: updatedTopics,
        allowed_topics_only: settings.allowed_topics_only,
        allowed_topics: settings.allowed_topics
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Error removing blocked topic:', error);
      alert('Failed to remove blocked topic');
    } finally {
      setSaving(false);
    }
  };

  const handleAddAllowedTopic = async () => {
    if (!newAllowedTopic.trim()) return;
    setSaving(true);
    try {
      const updatedTopics = [...settings.allowed_topics, newAllowedTopic.trim()];
      const response = await childSettingsAPI.manageTopics(selectedChild.id, {
        blocked_topics: settings.blocked_topics,
        allowed_topics_only: settings.allowed_topics_only,
        allowed_topics: updatedTopics
      });
      setSettings(response.data);
      setNewAllowedTopic('');
    } catch (error) {
      console.error('Error adding allowed topic:', error);
      alert('Failed to add allowed topic');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveAllowedTopic = async (index) => {
    setSaving(true);
    try {
      const updatedTopics = settings.allowed_topics.filter((_, i) => i !== index);
      const response = await childSettingsAPI.manageTopics(selectedChild.id, {
        blocked_topics: settings.blocked_topics,
        allowed_topics_only: settings.allowed_topics_only,
        allowed_topics: updatedTopics
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Error removing allowed topic:', error);
      alert('Failed to remove allowed topic');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleWhitelist = async () => {
    setSaving(true);
    try {
      const response = await childSettingsAPI.manageTopics(selectedChild.id, {
        blocked_topics: settings.blocked_topics,
        allowed_topics_only: !settings.allowed_topics_only,
        allowed_topics: settings.allowed_topics
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Error toggling whitelist:', error);
      alert('Failed to toggle whitelist mode');
    } finally {
      setSaving(false);
    }
  };

  if (!selectedChild) {
    return <div className="empty-state"><h3>Please select a child</h3></div>;
  }

  if (loading) {
    return <div className="loading-spinner">Loading controls...</div>;
  }

  return (
    <div>
      <h3>Parent Controls</h3>

      {/* AI Toggle */}
      <div style={{ marginBottom: '30px' }}>
        <h4>{APP_NAME}</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button
            onClick={handleToggleAI}
            disabled={saving}
            className="button"
            style={{
              background: settings.ai_enabled ? '#27ae60' : '#e74c3c',
              color: 'white',
              padding: '10px 20px'
            }}
          >
            {settings.ai_enabled ? 'AI Enabled ✓' : 'AI Disabled ✗'}
          </button>
          <span style={{ fontSize: '14px', color: '#666' }}>
            {settings.ai_enabled ? 'Child can chat with AI companion' : 'AI conversations are disabled'}
          </span>
        </div>
      </div>

      {/* Session Limits */}
      <div style={{ marginBottom: '30px' }}>
        <h4>Session Limits</h4>
        <form onSubmit={handleSetLimits} style={{ display: 'flex', gap: '15px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label>Daily Time Limit (minutes)</label>
            <input
              type="number"
              name="timeLimit"
              className="input"
              defaultValue={settings.daily_time_limit_minutes || ''}
              placeholder="Unlimited"
              min="1"
              style={{ width: '150px' }}
            />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label>Daily Message Limit</label>
            <input
              type="number"
              name="messageLimit"
              className="input"
              defaultValue={settings.daily_message_limit || ''}
              placeholder="Unlimited"
              min="1"
              style={{ width: '150px' }}
            />
          </div>
          <button type="submit" className="button button-primary" disabled={saving}>
            Update Limits
          </button>
        </form>

        {usage && (
          <div style={{ marginTop: '15px', padding: '15px', background: '#f8f9fa', borderRadius: '4px' }}>
            <strong>Today's Usage:</strong>
            <ul style={{ margin: '10px 0 0 0', paddingLeft: '20px' }}>
              <li>Messages: {usage.messages_today} {usage.daily_message_limit ? `/ ${usage.daily_message_limit}` : ''}</li>
              <li>Time: {usage.time_today_minutes} minutes {usage.daily_time_limit_minutes ? `/ ${usage.daily_time_limit_minutes}` : ''}</li>
              <li>Status: {usage.limit_reached ? <span style={{ color: '#e74c3c' }}>Limit Reached</span> : <span style={{ color: '#27ae60' }}>Active</span>}</li>
            </ul>
          </div>
        )}
      </div>

      {/* Topic Management */}
      <div style={{ marginBottom: '30px' }}>
        <h4>Topic Management</h4>

        {/* Whitelist Mode Toggle */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={settings.allowed_topics_only}
              onChange={handleToggleWhitelist}
              disabled={saving}
            />
            <span>Whitelist Mode (Only allow specific topics)</span>
          </label>
        </div>

        {/* Blocked Topics */}
        <div style={{ marginBottom: '20px' }}>
          <h5>Blocked Topics</h5>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
            <input
              type="text"
              className="input"
              placeholder="Add topic to block..."
              value={newBlockedTopic}
              onChange={(e) => setNewBlockedTopic(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddBlockedTopic())}
              style={{ flex: 1 }}
            />
            <button onClick={handleAddBlockedTopic} className="button button-primary" disabled={saving}>
              Add
            </button>
          </div>
          <div className="tag-list">
            {settings.blocked_topics.map((topic, index) => (
              <span key={index} className="tag" style={{ background: '#e74c3c' }}>
                {topic}
                <button onClick={() => handleRemoveBlockedTopic(index)} disabled={saving}>×</button>
              </span>
            ))}
            {settings.blocked_topics.length === 0 && (
              <p style={{ color: '#999', fontSize: '14px' }}>No blocked topics</p>
            )}
          </div>
        </div>

        {/* Allowed Topics (Whitelist) */}
        {settings.allowed_topics_only && (
          <div style={{ marginBottom: '20px' }}>
            <h5>Allowed Topics (Whitelist)</h5>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
              <input
                type="text"
                className="input"
                placeholder="Add allowed topic..."
                value={newAllowedTopic}
                onChange={(e) => setNewAllowedTopic(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddAllowedTopic())}
                style={{ flex: 1 }}
              />
              <button onClick={handleAddAllowedTopic} className="button button-primary" disabled={saving}>
                Add
              </button>
            </div>
            <div className="tag-list">
              {settings.allowed_topics.map((topic, index) => (
                <span key={index} className="tag" style={{ background: '#27ae60' }}>
                  {topic}
                  <button onClick={() => handleRemoveAllowedTopic(index)} disabled={saving}>×</button>
                </span>
              ))}
              {settings.allowed_topics.length === 0 && (
                <p style={{ color: '#999', fontSize: '14px' }}>No allowed topics (all topics will be blocked)</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Parent Settings Component (Account Settings)
function ParentSettings() {
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const loadPreferences = useCallback(async () => {
    setLoading(true);
    try {
      const response = await emailPreferencesAPI.get();
      setPreferences(response.data);
    } catch (error) {
      console.error('Error loading email preferences:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPreferences();
  }, [loadPreferences]);

  const handleToggle = async (preferenceKey) => {
    try {
      setSaving(true);
      setMessage(null);

      const updatedPreferences = {
        ...preferences,
        [preferenceKey]: !preferences[preferenceKey]
      };

      await emailPreferencesAPI.update(updatedPreferences);
      setPreferences(updatedPreferences);

      setMessage({
        type: 'success',
        text: 'Email preferences updated successfully!'
      });

      // Clear message after 3 seconds
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error updating preferences:', error);
      setMessage({
        type: 'error',
        text: 'Failed to update preferences. Please try again.'
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading-spinner">Loading account settings...</div>;
  }

  return (
    <div className="parent-settings">
      <h3>Account Settings</h3>

      {message && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="settings-section">
        <h4>Email Preferences</h4>
        <p className="section-description">
          Manage which automated emails you'd like to receive. You can change these preferences at any time.
        </p>

        <div className="preference-item">
          <div className="preference-info">
            <div className="preference-title">
              📊 Weekly Progress Reports
            </div>
            <div className="preference-description">
              Receive a summary of your child's weekly activity, engagement scores, and progress every Sunday.
            </div>
          </div>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={preferences.email_weekly_reports}
              onChange={() => handleToggle('email_weekly_reports')}
              disabled={saving}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>

        <div className="preference-item">
          <div className="preference-info">
            <div className="preference-title">
              👋 Re-engagement Reminders
            </div>
            <div className="preference-description">
              Get notified if your child hasn't used the app in a while, encouraging regular practice.
            </div>
          </div>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={preferences.email_churn_alerts}
              onChange={() => handleToggle('email_churn_alerts')}
              disabled={saving}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>

        <div className="preference-item">
          <div className="preference-info">
            <div className="preference-title">
              📢 Marketing & Updates
            </div>
            <div className="preference-description">
              Receive news, updates, and tips about Autism Companion and new features.
            </div>
          </div>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={preferences.email_marketing}
              onChange={() => handleToggle('email_marketing')}
              disabled={saving}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>

        <div className="info-box">
          <strong>🚨 Safety Alerts</strong>
          <p>
            Safety alerts are always enabled and cannot be disabled. These critical notifications
            ensure you're immediately informed of any safety concerns detected during conversations.
          </p>
        </div>

        <div className="compliance-note">
          <p>
            <strong>Privacy & Compliance:</strong> We respect your inbox. You can unsubscribe from
            any automated emails at any time using the link in the email footer or by adjusting these
            preferences. For more information, see our{' '}
            <a href="/privacy-policy" target="_blank">Privacy Policy</a>.
          </p>
        </div>
      </div>
    </div>
  );
}

// Main Dashboard Component
function Dashboard() {
  const { user, logout } = useAuth();
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddChild, setShowAddChild] = useState(false);
  const [newChildName, setNewChildName] = useState('');
  const [newChildAge, setNewChildAge] = useState('');
  const location = useLocation();

  const loadChildren = useCallback(async () => {
    setLoading(true);
    try {
      const response = await childrenAPI.getAll();
      setChildren(response.data);
      if (response.data.length > 0 && !selectedChild) {
        setSelectedChild(response.data[0]);
      }
    } catch (error) {
      console.error('Error loading children:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedChild]);

  useEffect(() => {
    loadChildren();
  }, [loadChildren]);

  const handleAddChild = async (e) => {
    e.preventDefault();
    try {
      await childrenAPI.create(newChildName, parseInt(newChildAge));
      setNewChildName('');
      setNewChildAge('');
      setShowAddChild(false);
      await loadChildren();
    } catch (error) {
      console.error('Error adding child:', error);
      alert('Failed to add child');
    }
  };

  if (loading) {
    return <div className="loading">Loading dashboard...</div>;
  }

  return (
    <div className="dashboard">
      <aside className="dashboard-sidebar">
        <div className="dashboard-header">
          <h2>Parent Dashboard</h2>
          <p>{user?.email}</p>
        </div>

        <nav>
          <ul className="dashboard-nav">
            <li>
              <Link to="/dashboard" className={location.pathname === '/dashboard' ? 'active' : ''}>
                Overview
              </Link>
            </li>
            <li>
              <Link to="/dashboard/summaries" className={location.pathname === '/dashboard/summaries' ? 'active' : ''}>
                Summaries
              </Link>
            </li>
            <li>
              <Link to="/dashboard/safety" className={location.pathname === '/dashboard/safety' ? 'active' : ''}>
                Safety Alerts
              </Link>
            </li>
            <li>
              <Link to="/dashboard/profile" className={location.pathname === '/dashboard/profile' ? 'active' : ''}>
                Child Profile
              </Link>
            </li>
            <li>
              <Link to="/dashboard/history" className={location.pathname === '/dashboard/history' ? 'active' : ''}>
                Conversation History
              </Link>
            </li>
            <li>
              <Link to="/dashboard/controls" className={location.pathname === '/dashboard/controls' ? 'active' : ''}>
                Parent Controls
              </Link>
            </li>
            <li>
              <Link to="/dashboard/settings" className={location.pathname === '/dashboard/settings' ? 'active' : ''}>
                Account Settings
              </Link>
            </li>
          </ul>
        </nav>

        <button onClick={logout} className="logout-button">
          Logout
        </button>
      </aside>

      <main className="dashboard-content">
        <div className="dashboard-section">
          <h3>Select Child</h3>
          <div className="child-selector">
            {children.map((child) => (
              <button
                key={child.id}
                onClick={() => setSelectedChild(child)}
                className={selectedChild?.id === child.id ? 'active' : ''}
              >
                {child.name} ({child.age || 'N/A'})
              </button>
            ))}
            <button onClick={() => setShowAddChild(!showAddChild)}>
              + Add Child
            </button>
          </div>

          {showAddChild && (
            <form onSubmit={handleAddChild} style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
              <input
                type="text"
                className="input"
                placeholder="Child's name"
                value={newChildName}
                onChange={(e) => setNewChildName(e.target.value)}
                required
                style={{ flex: 1 }}
              />
              <input
                type="number"
                className="input"
                placeholder="Age"
                value={newChildAge}
                onChange={(e) => setNewChildAge(e.target.value)}
                min="1"
                max="18"
                style={{ width: '100px' }}
              />
              <button type="submit" className="button button-primary">Add</button>
              <button type="button" onClick={() => setShowAddChild(false)} className="button button-secondary">Cancel</button>
            </form>
          )}
        </div>

        <div className="dashboard-section">
          <Routes>
            <Route path="/" element={<Overview selectedChild={selectedChild} children={children} />} />
            <Route path="/summaries" element={<Summaries selectedChild={selectedChild} />} />
            <Route path="/safety" element={<SafetyAlerts selectedChild={selectedChild} />} />
            <Route path="/profile" element={<ChildProfile selectedChild={selectedChild} onUpdate={loadChildren} />} />
            <Route path="/history" element={<ConversationHistory selectedChild={selectedChild} />} />
            <Route path="/controls" element={<ParentControls selectedChild={selectedChild} />} />
            <Route path="/settings" element={<ParentSettings />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}

export default Dashboard;
